import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {mate} from 'genetix/Flip.js';
import {isNumber} from 'utils/IsNumber.js';
import Tone from 'tone';
import {setEffectParameterValues} from 'actions/index.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';

export default superclass => class extends superclass  {
    getJson(isPreset) {
        let json = {
            enabled: this.enabled,
            oscillatorType: this.oscillatorType,
            oscillatorFrequency: this.oscillatorFrequency,
            oscillatorLevel: this.oscillatorLevel,
            polyphony: this.polyphony,
            portamento: this.portamento,
            detune: this.detune,
            partials: this.partials,
            customOscillatorType: this.customOscillatorType,
            harmonics: this.harmonics,
            pulseWidth: this.pulseWidth,
            spread: this.spreadParameter.get(),
            count: this.countParameter.get(),
            ... this.getFMJson(isPreset)
        };

        json = saveParameterToJson(json, 'pulseWidthParameter', this.pulseWidthParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'enabledParameter', this.enabledParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'oscillatorLevelParameter', this.oscillatorLevelParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'detuneParameter', this.detuneParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'portamentoParameter', this.portamentoParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'oscillatorFrequencyParameter', this.oscillatorFrequencyParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'harmonicsParameter', this.harmonicsParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'oscillatorTypeParameter', this.oscillatorTypeParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'partialsParameter', this.partialsParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'customOscillatorTypeParameter', this.customOscillatorTypeParameter, undefined, isPreset);

        return json;
    }

    getFMJson(isPreset) {
        let json = {
            harmonicity: this.harmonicityParameter.get(true),
            modulationIndex: this.modulationIndexParameter.get(true),
            feedback: this.feedbackParameter.get(true),
            ampEnvelope: this.ampEnvelope.getJson(),
            partialCount: this.partialCountParameter.get(true),
            formants: this.getFormantsJson(),
        };

        json = saveParameterToJson(json, 'harmonicityParameter', this.harmonicityParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'modulationIndexParameter', this.modulationIndexParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'feedbackParameter', this.feedbackParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'partialCountParameter', this.partialCountParameter, undefined, isPreset);
        return json;
    }

    fromJson(json, ignoreId) {
        let a = new Date().getTime();
        if (json === undefined) {
            return {};
        }
        let bulk = this.fromFMJson(json, ignoreId);
        let fmTime = new Date().getTime(); 
        bulkUpdate(this.enabledParameter, json.enabled, bulk);
        bulkUpdate(this.pulseWidthParameter, json.pulseWidth, bulk);
        // synth no longer supports FAT oscillators
        let oscType = json.oscillatorType //; && json.oscillatorType.replace("fat", "");

        bulkUpdate(this.oscillatorFrequencyParameter, json.oscillatorFrequency, bulk);
        bulkUpdate(this.portamentoParameter, json.portamento, bulk);
        if (json.customOscillatorType) {
            bulkUpdate(this.customOscillatorTypeParameter, json.customOscillatorType, bulk);
        } else {
            bulkUpdate(this.oscillatorTypeParameter, oscType, bulk);
        }
        if (json.harmonics) {
            bulkUpdate(this.harmonicsParameter, json.harmonics, bulk);
        }

        if (json.partials !== undefined &&
            json.partials !== null) {
            if (this.oscillatorType === 'custom' &&
                !this.customOscillatorType) {
                bulkUpdate(this.partialsParameter, json.partials, bulk);
            }
        }

        if (json.spread) {
            bulkUpdate(this.spreadParameter, json.spread, bulk);
        }
        if (json.count) {
            bulkUpdate(this.countParameter, json.count, bulk);
        }

        bulk = {
            ... bulk,
            ... loadParameterFromJson(
                json, 'pulseWidthParameter', this.pulseWidthParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'enabledParameter', this.enabledParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'portamentoParameter', this.portamentoParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'detuneParameter', this.detuneParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'oscillatorFrequencyParameter', this.oscillatorFrequencyParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'oscillatorTypeParameter', this.oscillatorTypeParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'partialsParameter', this.partialsParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'customOscillatorTypeParameter', this.customOscillatorTypeParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'harmonicsParameter', this.harmonicsParameter, this.sequencerEngine, ignoreId, true)
        };

        if (isNumber(json.detune)) {
            bulkUpdate(this.detuneParameter, json.detune, bulk);
        }
        bulkUpdate(this.oscillatorLevelParameter, json.oscillatorLevel, bulk);
        
        bulk = {
            ... bulk,
            ... loadParameterFromJson(
                json, 'detuneParameter',
                this.detuneParameter, this.sequencerEngine, ignoreId),
            ... loadParameterFromJson(
                json, 'oscillatorLevelParameter',
                this.oscillatorLevelParameter, this.sequencerEngine, ignoreId)
        };
        return bulk;
    }

    fromFMJson(json, ignoreId) {
        if (json === undefined) {
            return {} ;
        }
        let bulk = {};

        if (json.harmonicity !== undefined) {
            bulkUpdate(this.harmonicityParameter, json.harmonicity, bulk);
        }
        if (json.partialCount !== undefined) {
            bulkUpdate(this.partialCountParameter, json.partialCount, bulk);
        }
        if (json.modulationIndex !== undefined) {
            bulkUpdate(this.modulationIndexParameter, json.modulationIndex, bulk);
        }
        if (json.feedback !== undefined) {
            bulkUpdate(this.feedbackParameter, json.feedback, bulk);
        }
        if (json.ampEnvelope !== undefined) {
            bulk = {
                ... bulk,
                ... this.ampEnvelope.fromJson(json.ampEnvelope)
            };
        }

        bulk = {
            ... bulk,
           ...loadParameterFromJson(
                json, 'feedbackParameter', this.feedbackParameter, this.sequencerEngine, ignoreId, true),
            ...loadParameterFromJson(
                json, 'partialCountParameter', this.partialCountParameter, this.sequencerEngine, ignoreId, true)
        };

        bulk = {
            ...bulk,
            ...loadParameterFromJson(
                json, 'harmonicityParameter', this.harmonicityParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(
                json, 'modulationIndexParameter', this.modulationIndexParameter, this.sequencerEngine, ignoreId, true)
        };

        let a = new Date().getTime();
        if ('formants' in json) {
            bulk = {... bulk, ... this.loadFormantsJson(json.formants, ignoreId)};
        } else {
            this.formants.forEach(
                formant => formant.ripMods());
            this.formants.forEach(
                formant =>
                    bulkUpdate(formant.enabledParameter, false, bulk));
        }
        let b = new Date().getTime();
        return bulk;
    }

    mateParameter(name, data, param1, probability) {
        mate(this.sequencerEngine, name, data, param1, probability);
    }

    mateJson(partner, probability) {
        this.mateParameter('enabled', partner, this.enabledParameter, probability);
        if (!this.enabled) {
            return;
        }
        this.enabled = true;
        this.mateParameter('polyphony', partner, this.polyphonyParameter, probability);
        this.mateParameter('oscillatorType', partner, this.oscillatorTypeParameter, probability);
        this.mateParameter('oscillatorFrequency', partner, this.oscillatorFrequencyParameter, probability);
        this.mateParameter('detune', partner, this.detuneParameter, probability);
        this.mateParameter('portamento', partner, this.portamentoParameter, probability);
    }
}
